import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Collapse, Slide, Stack, StackProps, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { createContextHardened } from 'src/Common/Helper/ContextHelper';
import { ArrowBack, ArrowForward, Cancel, RestartAlt, Save } from '@mui/icons-material';
import { LoadingMask } from '../LoadingMask';

export interface StepDefinition {
  title: string;
  contentCallback: () => React.JSX.Element;
  description?: React.JSX.Element;
}

const ButtonStack = ({ children, ...props }: StackProps): React.JSX.Element => (
  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent={'center'} alignItems={'center'} {...props}>
    {children}
  </Stack>
);

interface Context {
  currentStep?: number;
  previousStep?: number;
  isNextEnabled: boolean;
  isSaveEnabled: boolean;
  setStepCount: (count: number) => void;
  setStep: (stepIndex?: number) => void;
  stepNext: () => void;
  stepBack: () => void;
  enableNext: (isEnabled: boolean) => void;
  enableSave: (isEnabled: boolean) => void;
}
const [PanelContext, usePanel] = createContextHardened<Context>('PanelContext');
const PanelProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const [stepCount, setStepCount] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<number>();
  const [previousStep, setPreviousStep] = useState<number>();
  const [isNextEnabled, setNextEnabled] = useState<boolean>(false);
  const [isSaveEnabled, setSaveEnabled] = useState<boolean>(false);

  /**
   * Unmount resets everything; Just to be safe.
   */
  useEffect(
    () => () => {
      setStepCount(0);
      setCurrentStep(undefined);
      setPreviousStep(undefined);
      setNextEnabled(false);
      setSaveEnabled(false);
    },
    [],
  );

  const setStep = useCallback((stepIndex?: number) => {
    setCurrentStep((prev) => {
      if (stepIndex === undefined) {
        setPreviousStep(undefined);
        return undefined;
      } else {
        setPreviousStep(prev);
        const capped = Math.max(0, stepIndex);
        return capped;
      }
    });
  }, []);

  const stepNext = useCallback(() => {
    if (currentStep === undefined) {
      return;
    }
    setStep(Math.min(currentStep + 1, stepCount - 1));
  }, [currentStep, stepCount]);

  const stepBack = useCallback(() => {
    if (currentStep === undefined) {
      return;
    }
    setStep(Math.max(currentStep - 1, 0));
  }, [currentStep]);

  return (
    <PanelContext.Provider
      value={{
        currentStep,
        previousStep,
        isNextEnabled,
        isSaveEnabled,
        setStepCount,
        setStep,
        stepNext,
        stepBack,
        enableNext: setNextEnabled,
        enableSave: setSaveEnabled,
      }}
    >
      {children}
    </PanelContext.Provider>
  );
};
export { usePanel, PanelProvider };

interface Props {
  steps: StepDefinition[];
  onCancel: () => void;
  onSave?: () => void;
  onClose?: () => void;
  onReset?: () => void;
  initialStep?: number;
  /** Loads the content, shows cancelable button. Defaults to false */
  isLoading?: boolean;
  /** Only loads the content when ready. Defaults to true. */
  isReady?: boolean;
  hidePanelButtons?: boolean;
  alternativeStepperLabel?: boolean;
  buttonLabels?: {
    save?: ReactNode;
    close?: ReactNode;
  };
}

/**
 * Component to be used as basis for Panels within the ContentControl wrapper.
 * Renders the step content, the stepper itself (when more than one step),
 * and the close/back/next/save button row.
 *
 * When only one step is provided, the stepper-specific UI will not show.
 */
export const ContentPanel = ({
  steps,
  onCancel,
  onSave,
  onReset,
  initialStep,
  hidePanelButtons,
  buttonLabels,
  alternativeStepperLabel,
  isLoading,
  isReady,
}: Props): React.JSX.Element => {
  const { setStepCount, setStep, stepNext, stepBack, isSaveEnabled, isNextEnabled, currentStep, previousStep } =
    usePanel();
  const [stepCount, isStepper] = useMemo(() => [steps.length, steps.length > 1], [steps]);

  const _isReady = useMemo(() => isReady !== false && currentStep !== undefined, [isReady, currentStep]);

  useEffect(() => {
    setStepCount(stepCount);
    setStep(initialStep ?? 0);
    return () => {
      setStep(undefined);
      setStepCount(0);
    };
  }, []);

  const handleReset = useCallback(() => {
    if (onReset !== undefined) {
      onReset();
    }
    setStep(initialStep ?? 0);
  }, [initialStep, onReset]);

  const stepsElement = useMemo(() => {
    if (!_isReady) {
      return <LoadingMask />;
    }
    return (
      <Box sx={{ minHeight: 200, p: 1, pb: 0, pt: 2 }}>
        {steps.map((_, index) => {
          const isActive = index === currentStep;

          const stepContent = (
            <Collapse key={index} in={isActive} unmountOnExit>
              {isActive && steps[currentStep].contentCallback()}
            </Collapse>
          );

          if (currentStep === previousStep) {
            return stepContent;
          }

          return (
            <Slide key={index} in={isActive} direction={(previousStep ?? 0) > currentStep! ? 'right' : 'left'}>
              {stepContent}
            </Slide>
          );
        })}
      </Box>
    );
  }, [steps, currentStep, previousStep, _isReady]);

  const stepperElement = useMemo(
    () => (
      <Stepper alternativeLabel={alternativeStepperLabel} activeStep={currentStep} sx={{ px: 1 }}>
        {steps.map((step, idx) => {
          const isActive = currentStep === idx;
          return (
            <Step key={idx}>
              <StepLabel
                StepIconProps={{
                  sx: (theme) => ({
                    fontSize: 32,
                    color: (isActive ? theme.palette.secondary.main : theme.palette.action.active) + '!important',
                    '& .MuiStepIcon-text': {
                      fill: isActive ? theme.palette.secondary.contrastText : theme.palette.background.paper,
                    },
                  }),
                }}
              >
                <Typography>{step.title}</Typography>
                <Box sx={{ display: { md: 'block', xs: 'none' } }}>{step.description ?? <></>}</Box>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>
    ),
    [steps, alternativeStepperLabel, currentStep],
  );

  return (
    <Stack direction={'column'} alignItems={'stretch'} spacing={2}>
      {stepsElement}
      {isStepper && stepperElement}
      {!hidePanelButtons && (
        <Stack direction={'row'} justifyContent={'space-between'} spacing={2} sx={{ p: 2, pt: 0 }}>
          {/** close & reset */}
          <ButtonStack>
            <Button variant={'contained'} color={'primary'} onClick={onCancel} startIcon={<Cancel />}>
              {buttonLabels?.close ?? (isLoading ? 'Cancel' : 'Close')}
            </Button>

            {onReset !== undefined && (
              <Button variant="outlined" color="primary" onClick={handleReset} startIcon={<RestartAlt />}>
                Reset
              </Button>
            )}
          </ButtonStack>
          {/** back & next */}
          <ButtonStack>
            {isStepper && (
              <Button
                variant="contained"
                color="primary"
                disabled={currentStep === 0}
                onClick={stepBack}
                startIcon={<ArrowBack />}
              >
                Back
              </Button>
            )}

            {isStepper && (
              <Button
                variant="contained"
                color="primary"
                disabled={!isNextEnabled || (isStepper && currentStep === stepCount - 1)}
                onClick={stepNext}
                endIcon={<ArrowForward />}
              >
                Next
              </Button>
            )}
          </ButtonStack>
          {/** save */}
          <Box>
            {onSave !== undefined && (
              <Button variant="contained" color="primary" disabled={!isSaveEnabled} onClick={onSave} endIcon={<Save />}>
                {buttonLabels?.save ?? 'Save'}
              </Button>
            )}
          </Box>
        </Stack>
      )}
    </Stack>
  );
};
